import React from "react";

/**
 * Hand-drawn Riyadh skyline contour with Kingdom Centre and Al Faisaliah
 * silhouettes plus KAFD-style towers. Pure SVG, fully under our control
 * (no risky stock imagery). Use as a section divider or full-section
 * background at low opacity.
 *
 * Props:
 *   variant: "gold" (light on dark) | "dark" (dark on light)
 *   className: additional tailwind classes on the wrapper
 *   opacity: numeric opacity 0..1
 */
export default function SkylineDivider({
  variant = "gold",
  className = "",
  opacity = 0.35,
  height = 180,
}) {
  const stroke = variant === "gold" ? "#C9A961" : "#0F2A24";
  const strokeWidth = 0.9;
  return (
    <div
      className={`pointer-events-none select-none ${className}`}
      style={{ opacity }}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 1600 240"
        width="100%"
        height={height}
        preserveAspectRatio="none"
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="miter"
      >
        {/* ground line */}
        <line x1="0" y1="238" x2="1600" y2="238" opacity="0.5" />

        {/* --- Far left: KAFD-style tower cluster --- */}
        <rect x="10"  y="180" width="55"  height="58" />
        <rect x="70"  y="140" width="38"  height="98" />
        <rect x="112" y="160" width="30"  height="78" />
        <rect x="148" y="100" width="46"  height="138" />
        <rect x="200" y="130" width="34"  height="108" />
        {/* tower windows (hairline) */}
        <line x1="148" y1="130" x2="194" y2="130" strokeDasharray="2 4" opacity="0.55" />
        <line x1="148" y1="170" x2="194" y2="170" strokeDasharray="2 4" opacity="0.55" />

        {/* --- Mid-left cluster --- */}
        <rect x="250" y="160" width="42"  height="78" />
        <polygon points="298,238 322,80 346,238" />
        <rect x="352" y="150" width="30"  height="88" />
        <rect x="388" y="120" width="26"  height="118" />
        <rect x="420" y="180" width="60"  height="58" />

        {/* --- Al Faisaliah-inspired pyramid tower with sphere --- */}
        <polygon points="510,238 546,50 582,238" />
        <circle cx="546" cy="76" r="9" />
        <line x1="546" y1="50" x2="546" y2="238" opacity="0.4" />

        {/* mid buildings */}
        <rect x="600" y="150" width="34"  height="88" />
        <rect x="640" y="170" width="24"  height="68" />
        <rect x="668" y="130" width="42"  height="108" />
        <rect x="718" y="160" width="30"  height="78" />

        {/* --- Kingdom Centre-inspired inverted arch tower --- */}
        <path d="M 780 238 L 780 70 Q 780 40 810 40 L 858 40 Q 888 40 888 70 L 888 238" />
        {/* the iconic "hole" through the top */}
        <path d="M 800 105 Q 834 55 868 105" />
        <line x1="834" y1="40" x2="834" y2="238" opacity="0.3" />
        {/* hairline floors */}
        <line x1="782" y1="140" x2="886" y2="140" strokeDasharray="2 4" opacity="0.5" />
        <line x1="782" y1="180" x2="886" y2="180" strokeDasharray="2 4" opacity="0.5" />
        <line x1="782" y1="210" x2="886" y2="210" strokeDasharray="2 4" opacity="0.5" />

        {/* mid-right buildings */}
        <rect x="905"  y="140" width="30"  height="98" />
        <rect x="942"  y="170" width="20"  height="68" />
        <rect x="970"  y="120" width="46"  height="118" />
        <rect x="1024" y="155" width="28"  height="83" />
        <rect x="1058" y="180" width="52"  height="58" />

        {/* --- Right: mixed KAFD towers --- */}
        <polygon points="1130,238 1150,90 1170,238" />
        <rect x="1180" y="130" width="34"  height="108" />
        <line x1="1180" y1="160" x2="1214" y2="160" strokeDasharray="2 4" opacity="0.55" />
        <line x1="1180" y1="200" x2="1214" y2="200" strokeDasharray="2 4" opacity="0.55" />
        <rect x="1222" y="160" width="26"  height="78" />
        <rect x="1256" y="120" width="42"  height="118" />
        <rect x="1306" y="150" width="30"  height="88" />

        {/* tall slim spire */}
        <line x1="1350" y1="238" x2="1350" y2="30" />
        <polygon points="1344,60 1350,30 1356,60" fill={stroke} />

        <rect x="1370" y="160" width="34" height="78" />
        <rect x="1410" y="130" width="46" height="108" />
        <rect x="1462" y="170" width="28" height="68" />
        <rect x="1495" y="150" width="40" height="88" />
        <rect x="1540" y="180" width="50" height="58" />
      </svg>
    </div>
  );
}
